import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyContactBar, DesktopFloatingContact } from "@/components/StickyContactBar";
import { CookieManager } from "@/components/CookieManager";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SplashScreen } from "@/components/SplashScreen";
import { SettingsProvider } from "@/lib/useSettings";

import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import ProcessPage from "@/pages/ProcessPage";
import ContentHubPage from "@/pages/ContentHubPage";
import NewsPage from "@/pages/NewsPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import KVKKPage from "@/pages/KVKKPage";
import ConsultingPage from "@/pages/ConsultingPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Admin page - lazy loaded (only when /admin is visited)
const AdminPage = lazy(() => import("@/pages/AdminPage"));

// Remove Emergent badge
function useRemoveBadge() {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    removeBadge();
    const observer = new MutationObserver(() => removeBadge());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
  }, [pathname]);
  return null;
}

function App() {
  useRemoveBadge();
  return (
    <div className="App">
      <ErrorBoundary>
      <SettingsProvider>
      <BrowserRouter>
        <ScrollToTop />
        <SplashScreen>
        <Routes>
          {/* Admin - no header/footer */}
          <Route path="/admin" element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-[#1F6E7A]/20 border-t-[#1F6E7A] rounded-full animate-spin" /></div>}><AdminPage /></Suspense>} />
          {/* Public site */}
          <Route path="*" element={
            <div className="page-wrapper">
              <Header />
              <main className="page-content">
                <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/hizmetler" element={<ServicesPage />} />
                  <Route path="/hizmetler/:slug" element={<ServiceDetailPage />} />
                  <Route path="/surec" element={<ProcessPage />} />
                  <Route path="/icerikler" element={<ContentHubPage />} />
                  <Route path="/icerikler/:postId" element={<ContentHubPage />} />
                  <Route path="/haberler" element={<NewsPage />} />
                  <Route path="/hakkimizda" element={<AboutPage />} />
                  <Route path="/iletisim" element={<ContactPage />} />
                  <Route path="/danismanlik" element={<ConsultingPage />} />
                  <Route path="/kvkk" element={<KVKKPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </ErrorBoundary>
              </main>
              <Footer />
              <StickyContactBar />
              <DesktopFloatingContact />
              <CookieManager />
            </div>
          } />
        </Routes>
        </SplashScreen>
      </BrowserRouter>
      </SettingsProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
